import { confirmDialog, showDialog } from '@/utils/dialogs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Modal,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Switch,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { ADMIN_COLORS, AdminStatusPill } from '@/components/admin/AdminUI';
import { useTranslation } from '../../hooks/useTranslation';
import { useResponsive } from '../../hooks/useResponsive';
import { apiRequest } from '../../services/apiClient';
import FrameworkStepEditor from './FrameworkStepEditor';
import { SchoolFrameworkStep } from '../../types/schoolFramework';

interface FrameworkEditorModalProps {
  visible: boolean;
  schoolId: number;
  frameworkId: number;
  onClose: () => void;
  onSaved: () => void;
}

type FrameworkType = 'step_model' | 'competency_model';

export default function FrameworkEditorModal({
  visible,
  schoolId,
  frameworkId,
  onClose,
  onSaved,
}: FrameworkEditorModalProps) {
  const { t } = useTranslation();
  // ADM-18: phone = one scroll column with a step picker on top; tablet = step
  // list + editor (control panel below the editor); desktop = three columns.
  const { isPhone, isTablet } = useResponsive();
  const framed = Platform.OS === 'web' && !isPhone;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [nameTr, setNameTr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [frameworkType, setFrameworkType] = useState<FrameworkType>('step_model');
  const [isApproved, setIsApproved] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<SchoolFrameworkStep[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [stepFilter, setStepFilter] = useState('');

  const [nameTrFocused, setNameTrFocused] = useState(false);
  const [nameEnFocused, setNameEnFocused] = useState(false);
  const [filterFocused, setFilterFocused] = useState(false);

  const extractedDetailStats = useMemo(() => {
    return steps.reduce(
      (acc, step) => {
        acc.subSteps += step.sub_steps?.length || 0;
        acc.concepts += step.concepts?.length || 0;
        acc.positive += step.positive_markers?.length || 0;
        acc.negative += step.negative_markers?.length || 0;
        acc.questions += step.sample_questions?.length || 0;
        return acc;
      },
      { subSteps: 0, concepts: 0, positive: 0, negative: 0, questions: 0 },
    );
  }, [steps]);

  const filteredSteps = useMemo(() => {
    const q = stepFilter.trim().toLowerCase();
    if (!q) return steps.map((s, i) => ({ step: s, index: i }));
    return steps
      .map((s, i) => ({ step: s, index: i }))
      .filter(({ step }) => {
        const tr = step.name?.tr?.toLowerCase() || '';
        const en = step.name?.en?.toLowerCase() || '';
        const key = step.step_key?.toLowerCase() || '';
        return tr.includes(q) || en.includes(q) || key.includes(q);
      });
  }, [steps, stepFilter]);

  const loadFramework = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest({
        endpoint: `/school-documents/${schoolId}/frameworks/${frameworkId}`,
        method: 'GET',
      });
      setNameTr(data.name?.tr || '');
      setNameEn(data.name?.en || '');
      setFrameworkType((data.framework_type || 'step_model') as FrameworkType);
      setIsApproved(data.is_approved);
      setIsActive(data.is_active);
      setSteps(
        (data.steps || []).map((step: any, index: number) => ({
          ...step,
          step_key: step.step_key || `step_${index + 1}`,
          step_order: Number(step.step_order || index + 1),
          name: step.name || { tr: '', en: '' },
          description: step.description || '',
          sub_steps: step.sub_steps || [],
          concepts: step.concepts || [],
          positive_markers: step.positive_markers || [],
          negative_markers: step.negative_markers || [],
          sample_questions: step.sample_questions || [],
          icf_competency_mapping: step.icf_competency_mapping || null,
        })),
      );
      setSelectedIndex(0);
    } catch (error) {
      console.error('Error loading framework:', error);
      showDialog(t('common.error'), 'Failed to load framework');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [frameworkId, schoolId, t, onClose]);

  useEffect(() => {
    if (visible && frameworkId) loadFramework();
  }, [visible, frameworkId, loadFramework]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiRequest({
        endpoint: `/school-documents/${schoolId}/frameworks/${frameworkId}`,
        method: 'PUT',
        data: {
          name: { tr: nameTr, en: nameEn },
          framework_type: frameworkType,
          is_approved: isApproved,
          is_active: isActive,
          steps: steps.map((s, i) => ({
            step_key: s.step_key,
            step_order: i + 1,
            name: s.name,
            description: s.description,
            sub_steps: s.sub_steps || [],
            concepts: s.concepts || [],
            positive_markers: s.positive_markers || [],
            negative_markers: s.negative_markers || [],
            sample_questions: s.sample_questions || [],
            icf_competency_mapping: s.icf_competency_mapping || null,
          })),
        },
      });
      showDialog(
        t('common.success'),
        t('schoolManagement.frameworksSection.saveSuccess'),
      );
      onSaved();
      onClose();
    } catch (error) {
      console.error('Error saving framework:', error);
      showDialog(
        t('common.error'),
        t('schoolManagement.frameworksSection.saveError'),
      );
    } finally {
      setSaving(false);
    }
  };

  const updateStep = (index: number, updated: SchoolFrameworkStep) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? updated : s)));
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    const newSteps = [...steps];
    const target = index + direction;
    if (target < 0 || target >= newSteps.length) return;
    [newSteps[index], newSteps[target]] = [newSteps[target], newSteps[index]];
    setSteps(newSteps);
    setSelectedIndex(target);
  };

  const deleteStep = async (index: number) => {
    const confirmMsg = t('schoolManagement.frameworksSection.deleteStepConfirm');
    const doDelete = () => {
      setSteps((prev) => prev.filter((_, i) => i !== index));
      setSelectedIndex((sel) => Math.max(0, Math.min(sel, steps.length - 2)));
    };
    const confirmed = await confirmDialog(
      t('schoolManagement.frameworksSection.deleteStep'),
      confirmMsg,
      t('common.delete'),
      t('common.cancel'),
    );
    if (confirmed) doDelete();
  };

  const addStep = () => {
    const order = steps.length + 1;
    setSteps((prev) => [
      ...prev,
      {
        step_key: `step_${order}`,
        step_order: order,
        name: { tr: '', en: '' },
        description: null,
        sub_steps: [],
        concepts: [],
        positive_markers: [],
        negative_markers: [],
        sample_questions: [],
        icf_competency_mapping: null,
      },
    ]);
    setSelectedIndex(order - 1);
  };

  const selectedStep = steps[selectedIndex];

  const renderStepItem = (
    { step, index }: { step: SchoolFrameworkStep; index: number },
    horizontal = false,
  ) => {
    const active = index === selectedIndex;
    const conceptCount =
      (step.concepts?.length || 0) +
      (step.sub_steps?.length || 0);
    return (
      <TouchableOpacity
        key={step.step_key + '_' + index}
        onPress={() => setSelectedIndex(index)}
        style={[{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          padding: 10,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: active
            ? ADMIN_COLORS.primary
            : ADMIN_COLORS.border,
          backgroundColor: active
            ? ADMIN_COLORS.primaryFaint
            : ADMIN_COLORS.background,
        }, horizontal && { width: 208 }]}
      >
        <View
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            backgroundColor: active
              ? ADMIN_COLORS.primary
              : ADMIN_COLORS.surface,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text
            className="text-xs font-bold"
            style={{
              color: active
                ? '#FFFFFF'
                : ADMIN_COLORS.text.secondary,
            }}
          >
            {index + 1}
          </Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text
            className="text-sm font-semibold"
            style={{
              color: active
                ? ADMIN_COLORS.primary
                : ADMIN_COLORS.text.primary,
            }}
            numberOfLines={1}
          >
            {step.name?.tr ||
              step.name?.en ||
              step.step_key ||
              '—'}
          </Text>
          <Text
            className="text-xs"
            style={{
              color: ADMIN_COLORS.text.muted,
              marginTop: 1,
            }}
          >
            {conceptCount}{' '}
            {t(
              'schoolManagement.frameworksSection.conceptsShort',
            )}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const stepsEmptyState = (
    <View
      style={{
        padding: 18,
        borderRadius: 12,
        backgroundColor: ADMIN_COLORS.surface,
        alignItems: 'center',
        gap: 6,
      }}
    >
      <MaterialIcons
        name="search-off"
        size={24}
        color={ADMIN_COLORS.text.muted}
      />
      <Text
        className="text-xs text-center"
        style={{ color: ADMIN_COLORS.text.secondary }}
      >
        {steps.length === 0
          ? t(
              'schoolManagement.frameworksSection.noSteps',
            )
          : t('common.noResults')}
      </Text>
    </View>
  );

  // Search + "Add step"; shared by the side list and the phone step picker.
  const stepsToolbar = (
    <View
      style={{
        padding: 14,
        gap: 10,
        borderBottomWidth: 1,
        borderBottomColor: ADMIN_COLORS.borderSoft,
      }}
    >
      <Text
        className="text-xs font-semibold uppercase"
        style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.4 }}
      >
        {t('schoolManagement.frameworksSection.stepsHeading')}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          paddingHorizontal: 10,
          backgroundColor: ADMIN_COLORS.surface,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: filterFocused
            ? ADMIN_COLORS.primary
            : ADMIN_COLORS.border,
        }}
      >
        <MaterialIcons
          name="search"
          size={14}
          color={ADMIN_COLORS.text.muted}
        />
        <TextInput
          value={stepFilter}
          onChangeText={setStepFilter}
          onFocus={() => setFilterFocused(true)}
          onBlur={() => setFilterFocused(false)}
          placeholder={t(
            'schoolManagement.frameworksSection.searchSteps',
          )}
          placeholderTextColor={ADMIN_COLORS.text.muted}
          style={{
            flex: 1,
            paddingVertical: 8,
            fontSize: 13,
            color: ADMIN_COLORS.text.primary,
          }}
        />
      </View>
      <TouchableOpacity
        onPress={addStep}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          paddingVertical: 9,
          borderRadius: 10,
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: ADMIN_COLORS.primary,
          backgroundColor: ADMIN_COLORS.primaryFaint,
        }}
      >
        <MaterialIcons name="add" size={16} color={ADMIN_COLORS.primary} />
        <Text
          className="text-xs font-semibold"
          style={{ color: ADMIN_COLORS.primary }}
        >
          {t('schoolManagement.frameworksSection.addStep')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const editorContent = (
    <>
      {/* Framework metadata */}
      <View
        style={{
          borderRadius: 16,
          borderWidth: 1,
          borderColor: ADMIN_COLORS.border,
          backgroundColor: ADMIN_COLORS.background,
          padding: 16,
          gap: 14,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              backgroundColor: ADMIN_COLORS.primarySoft,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MaterialIcons
              name="info"
              size={16}
              color={ADMIN_COLORS.primary}
            />
          </View>
          <Text
            className="text-sm font-bold"
            style={{ color: ADMIN_COLORS.text.primary }}
          >
            {t(
              'schoolManagement.frameworksSection.frameworkMetaTitle',
            )}
          </Text>
        </View>

        <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
          <View style={{ flex: 1, minWidth: 200, gap: 6 }}>
            <Text
              className="text-xs font-semibold"
              style={{ color: ADMIN_COLORS.text.secondary }}
            >
              {t(
                'schoolManagement.frameworksSection.frameworkNameTr',
              )}
            </Text>
            <View
              style={{
                paddingHorizontal: 12,
                backgroundColor: ADMIN_COLORS.surface,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: nameTrFocused
                  ? ADMIN_COLORS.primary
                  : ADMIN_COLORS.border,
              }}
            >
              <TextInput
                value={nameTr}
                onChangeText={setNameTr}
                onFocus={() => setNameTrFocused(true)}
                onBlur={() => setNameTrFocused(false)}
                placeholder="Türkçe çerçeve adı"
                placeholderTextColor={ADMIN_COLORS.text.muted}
                style={{
                  paddingVertical: 10,
                  fontSize: 14,
                  color: ADMIN_COLORS.text.primary,
                }}
              />
            </View>
          </View>
          <View style={{ flex: 1, minWidth: 200, gap: 6 }}>
            <Text
              className="text-xs font-semibold"
              style={{ color: ADMIN_COLORS.text.secondary }}
            >
              {t(
                'schoolManagement.frameworksSection.frameworkNameEn',
              )}
            </Text>
            <View
              style={{
                paddingHorizontal: 12,
                backgroundColor: ADMIN_COLORS.surface,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: nameEnFocused
                  ? ADMIN_COLORS.primary
                  : ADMIN_COLORS.border,
              }}
            >
              <TextInput
                value={nameEn}
                onChangeText={setNameEn}
                onFocus={() => setNameEnFocused(true)}
                onBlur={() => setNameEnFocused(false)}
                placeholder="English framework name"
                placeholderTextColor={ADMIN_COLORS.text.muted}
                style={{
                  paddingVertical: 10,
                  fontSize: 14,
                  color: ADMIN_COLORS.text.primary,
                }}
              />
            </View>
          </View>
        </View>

        <View style={{ gap: 6 }}>
          <Text
            className="text-xs font-semibold"
            style={{ color: ADMIN_COLORS.text.secondary }}
          >
            {t(
              'schoolManagement.frameworksSection.frameworkType',
            )}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              gap: 8,
              padding: 4,
              borderRadius: 10,
              backgroundColor: ADMIN_COLORS.surface,
              borderWidth: 1,
              borderColor: ADMIN_COLORS.border,
            }}
          >
            {(['step_model', 'competency_model'] as const).map((ft) => {
              const active = ft === frameworkType;
              return (
                <TouchableOpacity
                  key={ft}
                  onPress={() => setFrameworkType(ft)}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    paddingVertical: 8,
                    borderRadius: 8,
                    backgroundColor: active
                      ? ADMIN_COLORS.background
                      : 'transparent',
                    borderWidth: active ? 1 : 0,
                    borderColor: active ? ADMIN_COLORS.border : 'transparent',
                  }}
                >
                  <MaterialIcons
                    name={ft === 'step_model' ? 'list-alt' : 'workspaces'}
                    size={14}
                    color={
                      active
                        ? ADMIN_COLORS.primary
                        : ADMIN_COLORS.text.secondary
                    }
                  />
                  <Text
                    className="text-xs font-semibold"
                    style={{
                      color: active
                        ? ADMIN_COLORS.primary
                        : ADMIN_COLORS.text.secondary,
                    }}
                  >
                    {ft === 'step_model'
                      ? t(
                          'schoolManagement.frameworksSection.typeStepModel',
                        )
                      : t(
                          'schoolManagement.frameworksSection.typeCompetencyModel',
                        )}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <View
            style={{
              padding: 10,
              borderRadius: 10,
              backgroundColor: ADMIN_COLORS.primaryFaint,
              borderWidth: 1,
              borderColor: ADMIN_COLORS.primaryFaintBorder,
              gap: 2,
            }}
          >
            <Text
              className="text-xs font-bold"
              style={{ color: ADMIN_COLORS.primary }}
            >
              {frameworkType === 'step_model'
                ? t(
                    'schoolManagement.frameworksSection.typeStepModel',
                  )
                : t(
                    'schoolManagement.frameworksSection.typeCompetencyModel',
                  )}
            </Text>
            <Text
              className="text-xs"
              style={{ color: ADMIN_COLORS.text.primary }}
            >
              {frameworkType === 'step_model'
                ? t(
                    'schoolManagement.frameworksSection.typeStepModelDescription',
                  )
                : t(
                    'schoolManagement.frameworksSection.typeCompetencyModelDescription',
                  )}
            </Text>
          </View>
        </View>
      </View>

      {/* Selected step editor */}
      {selectedStep ? (
        <View
          style={{
            borderRadius: 16,
            borderWidth: 1,
            borderColor: ADMIN_COLORS.border,
            backgroundColor: ADMIN_COLORS.background,
            overflow: 'hidden',
          }}
        >
          <FrameworkStepEditor
            step={selectedStep}
            frameworkType={frameworkType}
            index={selectedIndex}
            totalSteps={steps.length}
            onChange={(updated) => updateStep(selectedIndex, updated)}
            onMoveUp={() => moveStep(selectedIndex, -1)}
            onMoveDown={() => moveStep(selectedIndex, 1)}
            onDelete={() => deleteStep(selectedIndex)}
          />
        </View>
      ) : (
        <View
          style={{
            padding: 30,
            borderRadius: 16,
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: ADMIN_COLORS.border,
            backgroundColor: ADMIN_COLORS.background,
            alignItems: 'center',
            gap: 10,
          }}
        >
          <View
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: ADMIN_COLORS.primarySoft,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MaterialIcons
              name="layers"
              size={26}
              color={ADMIN_COLORS.primary}
            />
          </View>
          <Text
            className="text-base font-bold"
            style={{ color: ADMIN_COLORS.text.primary }}
          >
            {t(
              'schoolManagement.frameworksSection.selectStepTitle',
            )}
          </Text>
          <Text
            className="text-sm text-center"
            style={{
              color: ADMIN_COLORS.text.secondary,
              maxWidth: 320,
            }}
          >
            {t(
              'schoolManagement.frameworksSection.selectStepBody',
            )}
          </Text>
        </View>
      )}
    </>
  );

  const controlPanelContent = (
    <>
      <Text
        className="text-xs font-semibold uppercase"
        style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.4 }}
      >
        {t(
          'schoolManagement.frameworksSection.controlPanel',
        )}
      </Text>

      {/* Extraction stats */}
      <View
        style={{
          borderRadius: 14,
          borderWidth: 1,
          borderColor: ADMIN_COLORS.successBorder,
          backgroundColor: ADMIN_COLORS.successFaint,
          padding: 12,
          gap: 10,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <MaterialIcons
            name="auto-awesome"
            size={14}
            color={ADMIN_COLORS.successText}
          />
          <Text
            className="text-xs font-bold"
            style={{ color: ADMIN_COLORS.successText }}
          >
            {t(
              'schoolManagement.frameworksSection.extractionDetailsTitle',
            )}
          </Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {[
            {
              icon: 'subdirectory-arrow-right',
              label: t(
                'schoolManagement.frameworksSection.subStepsShort',
              ),
              value: extractedDetailStats.subSteps,
            },
            {
              icon: 'category',
              label: t(
                'schoolManagement.frameworksSection.conceptsShort',
              ),
              value: extractedDetailStats.concepts,
            },
            {
              icon: 'thumb-up-off-alt',
              label: t(
                'schoolManagement.frameworksSection.positiveShort',
              ),
              value: extractedDetailStats.positive,
            },
            {
              icon: 'trending-up',
              label: t(
                'schoolManagement.frameworksSection.negativeShort',
              ),
              value: extractedDetailStats.negative,
            },
          ].map((s) => (
            <View
              key={s.label}
              style={{
                flexBasis: '47%',
                padding: 8,
                borderRadius: 10,
                backgroundColor: ADMIN_COLORS.background,
                borderWidth: 1,
                borderColor: ADMIN_COLORS.successBorder,
                gap: 4,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <MaterialIcons
                  name={s.icon as any}
                  size={12}
                  color={ADMIN_COLORS.successText}
                />
                <Text
                  className="text-xs font-semibold"
                  style={{ color: ADMIN_COLORS.text.muted }}
                  numberOfLines={1}
                >
                  {s.label}
                </Text>
              </View>
              <Text
                className="text-base font-extrabold"
                style={{ color: ADMIN_COLORS.text.primary }}
              >
                {s.value}
              </Text>
            </View>
          ))}
        </View>
      </View>

      {/* Approval warning */}
      {!isApproved ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 8,
            padding: 12,
            borderRadius: 12,
            backgroundColor: ADMIN_COLORS.warningSoft,
            borderWidth: 1,
            borderColor: ADMIN_COLORS.warningBorder,
          }}
        >
          <MaterialIcons
            name="warning-amber"
            size={16}
            color={ADMIN_COLORS.warningText}
          />
          <Text
            className="text-xs flex-1"
            style={{ color: ADMIN_COLORS.warningText }}
          >
            {t(
              'schoolManagement.frameworksSection.approvalReminder',
            )}
          </Text>
        </View>
      ) : null}

      {/* Toggles */}
      <View
        style={{
          borderRadius: 14,
          borderWidth: 1,
          borderColor: ADMIN_COLORS.border,
          backgroundColor: ADMIN_COLORS.surface,
          padding: 12,
          gap: 12,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text
              className="text-sm font-semibold"
              style={{ color: ADMIN_COLORS.text.primary }}
            >
              {t(
                'schoolManagement.frameworksSection.activeLabel',
              )}
            </Text>
            <Text
              className="text-xs"
              style={{ color: ADMIN_COLORS.text.secondary }}
            >
              {t(
                'schoolManagement.frameworksSection.activeHint',
              )}
            </Text>
          </View>
          <Switch
            value={isActive}
            onValueChange={setIsActive}
            trackColor={{ false: '#d1d5db', true: ADMIN_COLORS.primary }}
            thumbColor="#ffffff"
          />
        </View>
        <View
          style={{
            height: 1,
            backgroundColor: ADMIN_COLORS.borderSoft,
          }}
        />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text
              className="text-sm font-semibold"
              style={{ color: ADMIN_COLORS.text.primary }}
            >
              {t(
                'schoolManagement.frameworksSection.approvedLabel',
              )}
            </Text>
            <Text
              className="text-xs"
              style={{ color: ADMIN_COLORS.text.secondary }}
            >
              {t(
                'schoolManagement.frameworksSection.approvedHint',
              )}
            </Text>
          </View>
          <Switch
            value={isApproved}
            onValueChange={setIsApproved}
            trackColor={{ false: '#d1d5db', true: ADMIN_COLORS.success }}
            thumbColor="#ffffff"
          />
        </View>
      </View>
    </>
  );


  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(15, 23, 42, 0.55)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: framed ? 16 : 0,
        }}
      >
        <View
          style={{
            width: '100%',
            maxWidth: 1080,
            height: '100%',
            maxHeight: framed ? '95%' : '100%',
            backgroundColor: ADMIN_COLORS.background,
            borderRadius: framed ? 20 : 0,
            borderWidth: framed ? 1 : 0,
            borderColor: ADMIN_COLORS.border,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              padding: isPhone ? 12 : 18,
              borderBottomWidth: 1,
              borderBottomColor: ADMIN_COLORS.borderSoft,
              backgroundColor: ADMIN_COLORS.background,
              flexWrap: 'wrap',
            }}
          >
            <TouchableOpacity
              onPress={onClose}
              hitSlop={8}
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: ADMIN_COLORS.surface,
              }}
            >
              <MaterialIcons name="close" size={20} color={ADMIN_COLORS.text.primary} />
            </TouchableOpacity>

            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                backgroundColor: ADMIN_COLORS.primarySoft,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialIcons name="account-tree" size={20} color={ADMIN_COLORS.primary} />
            </View>

            <View style={{ flex: 1, minWidth: 180 }}>
              <Text
                className="text-base font-bold"
                style={{ color: ADMIN_COLORS.text.primary }}
                numberOfLines={1}
              >
                {t('schoolManagement.frameworksSection.editorTitle')}
              </Text>
              <Text
                className="text-xs"
                style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
                numberOfLines={1}
              >
                {nameTr || nameEn || '—'}
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
              <AdminStatusPill
                tone={isActive ? 'success' : 'neutral'}
                icon={isActive ? 'check-circle' : 'pause-circle-outline'}
                label={
                  isActive
                    ? t('schoolManagement.frameworksSection.active')
                    : t('schoolManagement.frameworksSection.inactive')
                }
              />
              <AdminStatusPill
                tone={isApproved ? 'primary' : 'warning'}
                icon={isApproved ? 'verified' : 'schedule'}
                label={
                  isApproved
                    ? t('schoolManagement.frameworksSection.approved')
                    : t('schoolManagement.frameworksSection.pendingApproval')
                }
              />
              <AdminStatusPill
                tone="neutral"
                icon="layers"
                label={`${steps.length} ${t('schoolManagement.frameworksSection.stepsShort')}`}
              />
            </View>

            {/* Phone: Save lives in the footer; Preview is not built yet. */}
            {isPhone ? null : (
              <>
                {/* Önizle (preview) — secondary action that opens a read-only
                    view of the framework. Hidden until preview surfaces ship,
                    but the button is in the chrome to match the mockup. */}
                <TouchableOpacity
                  disabled={saving || loading}
                  onPress={() => {
                    /* TODO: open framework preview drawer */
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    paddingHorizontal: 14,
                    paddingVertical: 10,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: ADMIN_COLORS.border,
                    backgroundColor: ADMIN_COLORS.background,
                    opacity: saving || loading ? 0.5 : 1,
                  }}
                >
                  <MaterialIcons
                    name="visibility"
                    size={16}
                    color={ADMIN_COLORS.text.primary}
                  />
                  <Text
                    className="text-sm font-semibold"
                    style={{ color: ADMIN_COLORS.text.primary }}
                  >
                    {t('schoolManagement.frameworksSection.preview')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  disabled={saving || loading}
                  onPress={handleSave}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    paddingHorizontal: 18,
                    paddingVertical: 10,
                    borderRadius: 12,
                    backgroundColor: saving || loading ? '#93C5FD' : ADMIN_COLORS.primary,
                  }}
                >
                  {saving ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <MaterialIcons name="save" size={16} color="#FFFFFF" />
                  )}
                  <Text className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                    {t('schoolManagement.frameworksSection.save')}
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>

          {loading ? (
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
              }}
            >
              <ActivityIndicator size="large" color={ADMIN_COLORS.primary} />
              <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
                {t('schoolManagement.frameworksSection.loading')}
              </Text>
            </View>
          ) : (
            <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surface }}>
              {isPhone ? (
                // Phone (<760): one scroll column with a horizontal step
                // picker on top, then the editor, then the control panel.
                <ScrollView
                  testID="framework-editor-phone"
                  contentContainerStyle={{ padding: 12, gap: 14 }}
                >
                  <View
                    style={{
                      borderRadius: 16,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                      overflow: 'hidden',
                    }}
                  >
                    {stepsToolbar}
                    {filteredSteps.length === 0 ? (
                      <View style={{ padding: 10 }}>{stepsEmptyState}</View>
                    ) : (
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{ padding: 10, gap: 8 }}
                      >
                        {filteredSteps.map((item) => renderStepItem(item, true))}
                      </ScrollView>
                    )}
                  </View>
                  {editorContent}
                  {controlPanelContent}
                </ScrollView>
              ) : (
                <View style={{ flex: 1, flexDirection: 'row' }}>
                  {/* Left column — Steps list */}
                  <View
                    style={{
                      width: isTablet ? 240 : 260,
                      borderRightWidth: 1,
                      borderRightColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                    }}
                  >
                    {stepsToolbar}
                    <ScrollView contentContainerStyle={{ padding: 10, gap: 6 }}>
                      {filteredSteps.length === 0
                        ? stepsEmptyState
                        : filteredSteps.map((item) => renderStepItem(item))}
                    </ScrollView>
                  </View>

                  {/* Center column — Editor (tablet: control panel below it) */}
                  <View style={{ flex: 1, minWidth: 0, backgroundColor: ADMIN_COLORS.surface }}>
                    <ScrollView contentContainerStyle={{ padding: 16, gap: 14 }}>
                      {editorContent}
                      {isTablet ? controlPanelContent : null}
                    </ScrollView>
                  </View>

                  {/* Right column — Control panel (desktop) */}
                  {isTablet ? null : (
                    <View
                      style={{
                        width: 280,
                        borderLeftWidth: 1,
                        borderLeftColor: ADMIN_COLORS.border,
                        backgroundColor: ADMIN_COLORS.background,
                      }}
                    >
                      <ScrollView contentContainerStyle={{ padding: 14, gap: 14 }}>
                        {controlPanelContent}
                      </ScrollView>
                    </View>
                  )}
                </View>
              )}
            </View>
          )}

          {/* Footer */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              padding: 14,
              borderTopWidth: 1,
              borderTopColor: ADMIN_COLORS.border,
              backgroundColor: ADMIN_COLORS.background,
              flexWrap: 'wrap',
            }}
          >
            <View
              style={{
                flex: 1,
                minWidth: 200,
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <MaterialIcons
                name="schedule"
                size={14}
                color={ADMIN_COLORS.text.secondary}
              />
              <Text className="text-xs" style={{ color: ADMIN_COLORS.text.secondary }}>
                {t(
                  'schoolManagement.frameworksSection.lastSaved',
                )}
              </Text>
              <Text className="text-xs" style={{ color: ADMIN_COLORS.text.muted }}>
                ·
              </Text>
              <Text className="text-xs" style={{ color: ADMIN_COLORS.text.secondary }}>
                {t(
                  'schoolManagement.frameworksSection.footerHint',
                )}
              </Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <TouchableOpacity
                onPress={onClose}
                disabled={saving}
                style={{
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: ADMIN_COLORS.border,
                  backgroundColor: ADMIN_COLORS.background,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <Text
                  className="text-sm font-semibold"
                  style={{ color: ADMIN_COLORS.text.primary }}
                >
                  {t('common.cancel')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                disabled={saving || loading}
                onPress={handleSave}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 12,
                  backgroundColor: saving || loading ? '#93C5FD' : ADMIN_COLORS.primary,
                }}
              >
                {saving ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <MaterialIcons name="save" size={16} color="#FFFFFF" />
                )}
                <Text className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                  {t('schoolManagement.frameworksSection.save')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}
